import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  Alert,
  Breadcrumbs,
  Button,
  Stack,
  TextField,
  Typography,
  Box,
  Tab,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Switch,
} from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import GridViewIcon from "@mui/icons-material/GridView";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import Div from "@jumbo/shared/Div";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import AddBoxIcon from "@mui/icons-material/AddBox";
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import Autocomplete from "@mui/material/Autocomplete";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { getDSOKProducts } from "app/services/api/management";
import { putMenuBoard, getMenuBoard } from "app/services/api/menuBoard";
import MenuBoardProductRow from "./MenuBoardProductRow";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

function normalizeCustomerLanguages(customer, t) {
  const raw =
    customer?.productLanguages ||
    customer?.dsokSettings?.customer?.languages ||
    [];
  if (!Array.isArray(raw) || raw.length === 0) {
    return [
      { code: "sk-SK", label: "Slovenčina" },
      { code: "en-US", label: "English" },
    ];
  }
  return raw.map((item) => {
    if (typeof item === "string") {
      return {
        code: item,
        label: t(`languages.${item}`, { defaultValue: item }),
      };
    }
    return {
      code: item.code || item,
      label: item.label || item.name || item.code || String(item.code || item),
    };
  });
}

function generateSectionId() {
  return `mb-sec-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function generateItemId() {
  return `mb-item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getOptionDisplayName(opt, lang) {
  if (!opt) return "";
  const name =
    opt?.title?.[lang] ||
    opt?.productName?.[lang] ||
    opt?.title?.["sk-SK"] ||
    opt?.productName?.["sk-SK"] ||
    opt?.title?.["en-US"] ||
    opt?.productName?.["en-US"];
  if (typeof name === "string" && name) return name;
  const obj = opt?.productName ?? opt?.title;
  if (obj && typeof obj === "object") {
    const v = Object.values(obj).find((x) => typeof x === "string" && x);
    if (v) return v;
  }
  return opt?.name || opt?.id || opt?._id || "";
}

function getProductImageUrl(p) {
  if (!p) return "";
  const candidates = [
    p?.imageUrl,
    p?.image,
    p?.customProductImageUrl,
    p?.mainImage,
    p?.productImage,
    p?.metadata?.image,
    Array.isArray(p?.images) && p.images[0] ? p.images[0] : null,
  ];
  for (const v of candidates) {
    if (typeof v === "string" && v) return v;
    if (v && typeof v === "object" && (v?.url ?? v?.src ?? v?.imageUrl)) {
      const u = v.url ?? v.src ?? v.imageUrl;
      if (typeof u === "string") return u;
    }
  }
  return "";
}

function normalizeDsokProductsList(products) {
  if (!products) return [];
  const arr = Array.isArray(products)
    ? products
    : Object.keys(products || {}).map((k) => ({
        ...(products[k] || {}),
        _id: products[k]?._id ?? k,
      }));
  return arr.map((p) => ({ ...p, id: p._id ?? p.id }));
}

function dsokProductToBoardItem(p, lang) {
  const productId = p?.id ?? p?._id;
  if (!productId) return null;
  return {
    id: generateItemId(),
    productId,
    nameOverrideByLang: null,
    priceOverride: null,
    imageUrl: getProductImageUrl(p) || null,
    baseName: getOptionDisplayName(p, lang) || String(productId),
    basePrice: p?.price ?? p?.salePrice ?? 0,
  };
}

const INITIAL_FORM = {
  boardName: "",
  displayType: "list",
  displayLanguagesCount: 1,
  currency: "€",
  showProductDescription: false,
  showProductImages: true,
  sections: [],
};

const MenuBoardEdit = () => {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { getAuthUser } = useJumboAuth();
  const customer = getAuthUser()?.customer;
  const lang = i18n.language?.startsWith("sk") ? "sk-SK" : "en-US";

  const isCreate = boardId === "new" || !boardId;
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [selectedTab, setSelectedTab] = useState("settings");
  const [dsokProducts, setDsokProducts] = useState([]);
  const [autocompleteKeys, setAutocompleteKeys] = useState({});
  const [selectedLanguageCodes, setSelectedLanguageCodes] = useState([]);
  const languagesInitializedRef = useRef(false);

  const allCustomerLanguages = useMemo(
    () => normalizeCustomerLanguages(customer, t),
    [customer, t]
  );

  useEffect(() => {
    if (
      allCustomerLanguages.length > 0 &&
      selectedLanguageCodes.length === 0 &&
      !languagesInitializedRef.current
    ) {
      setSelectedLanguageCodes(allCustomerLanguages.map((l) => l.code));
      languagesInitializedRef.current = true;
    }
  }, [allCustomerLanguages, selectedLanguageCodes.length]);

  const languages = useMemo(() => {
    if (selectedLanguageCodes.length === 0) return allCustomerLanguages;
    return allCustomerLanguages.filter((l) =>
      selectedLanguageCodes.includes(l.code)
    );
  }, [allCustomerLanguages, selectedLanguageCodes]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    let cancelled = false;
    getDSOKProducts()
      .then((products) => {
        if (!cancelled) setDsokProducts(normalizeDsokProductsList(products));
      })
      .catch(() => {
        if (!cancelled) setDsokProducts([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isCreate) {
      setForm(INITIAL_FORM);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getMenuBoard(boardId);
        if (cancelled) return;
        const board = data?.menuBoard ?? data?.data ?? data;
        const langs = normalizeCustomerLanguages(getAuthUser()?.customer, t);
        const sections = Array.isArray(board?.sections)
          ? board.sections.map((sec) => ({
              id: sec.id || generateSectionId(),
              titleByLang:
                sec.titleByLang ||
                langs.reduce((acc, l) => ({ ...acc, [l.code]: "" }), {}),
              items: Array.isArray(sec.items) ? sec.items : [],
            }))
          : [];
        setForm({
          boardName: board?.boardName ?? board?.name ?? "",
          displayType: board?.displayType ?? "list",
          displayLanguagesCount:
            typeof board?.displayLanguagesCount === "number" &&
            board.displayLanguagesCount >= 1
              ? Math.min(Math.floor(board.displayLanguagesCount), 3600)
              : 1,
          currency: (board?.currency ?? "€").toString().slice(0, 3),
          showProductDescription: !!board?.showProductDescription,
          showProductImages: board?.showProductImages !== false,
          sections,
        });
      } catch (e) {
        if (!cancelled) {
          setForm(INITIAL_FORM);
          enqueueSnackbar(
            t("notifications.wasFailed", { defaultValue: "Failed to load." }),
            { variant: "error" }
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [boardId, isCreate, enqueueSnackbar, t]);

  const handleChange = (field) => (e) => {
    let value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    if (field === "currency" && typeof value === "string") {
      value = value.slice(0, 3);
    }
    if (field === "displayLanguagesCount") {
      const num = parseInt(value, 10);
      value = Number.isFinite(num) ? Math.min(3600, Math.max(1, num)) : 1;
    }
    setForm((prev) => ({ ...prev, [field]: value }));
    setSaveError(null);
  };

  const handleAddSection = useCallback(() => {
    const langs =
      Array.isArray(languages) && languages.length > 0
        ? languages
        : [
            { code: "sk-SK", label: "Slovenčina" },
            { code: "en-US", label: "English" },
          ];
    const titleByLang = langs.reduce(
      (acc, l) => ({ ...acc, [l.code]: "" }),
      {}
    );
    setForm((prev) => ({
      ...prev,
      sections: [
        ...(prev.sections || []),
        { id: generateSectionId(), titleByLang, items: [] },
      ],
    }));
  }, [languages]);

  const handleSectionTitleChange = useCallback((sectionId, langCode, value) => {
    setForm((prev) => ({
      ...prev,
      sections: prev.sections.map((sec) =>
        sec.id === sectionId
          ? {
              ...sec,
              titleByLang: { ...(sec.titleByLang || {}), [langCode]: value },
            }
          : sec
      ),
    }));
  }, []);

  const handleAddProductToSection = useCallback(
    (sectionId, product) => {
      const productId = product?.id ?? product?._id;
      if (!productId) return;
      const section = form.sections.find((s) => s.id === sectionId);
      const exists = section?.items.some((it) => it.productId === productId);
      if (exists) {
        enqueueSnackbar(
          t("pages.menuManager.productAlreadyInSection", {
            defaultValue: "Product already in this section.",
          }),
          { variant: "warning" }
        );
        return;
      }
      const newItem = dsokProductToBoardItem(product, lang);
      setForm((prev) => ({
        ...prev,
        sections: prev.sections.map((s) =>
          s.id === sectionId ? { ...s, items: [...s.items, newItem] } : s
        ),
      }));
      setAutocompleteKeys((k) => ({
        ...k,
        [sectionId]: (k[sectionId] || 0) + 1,
      }));
    },
    [form.sections, lang, enqueueSnackbar, t]
  );

  const handleRemoveProduct = useCallback((sectionId, itemId) => {
    setForm((prev) => ({
      ...prev,
      sections: prev.sections.map((s) =>
        s.id === sectionId
          ? { ...s, items: s.items.filter((it) => it.id !== itemId) }
          : s
      ),
    }));
  }, []);

  const handleUpdateProduct = useCallback((sectionId, itemId, field, value) => {
    setForm((prev) => ({
      ...prev,
      sections: prev.sections.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              items: s.items.map((it) =>
                it.id === itemId ? { ...it, [field]: value } : it
              ),
            }
          : s
      ),
    }));
  }, []);

  const handleReorderSections = useCallback((activeId, overId) => {
    if (activeId === overId) return;
    setForm((prev) => {
      const ids = prev.sections.map((s) => s.id);
      const oldIndex = ids.indexOf(activeId);
      const newIndex = ids.indexOf(overId);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const reordered = arrayMove(prev.sections, oldIndex, newIndex);
      return { ...prev, sections: reordered };
    });
  }, []);

  const allItemSortIds = useMemo(() => {
    const ids = [];
    form.sections.forEach((sec) => {
      sec.items.forEach((it) => ids.push(`${sec.id}|${it.id}`));
    });
    return ids;
  }, [form.sections]);

  const handleItemsDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const activeStr = String(active.id);
      const overStr = String(over.id);
      const activeParts = activeStr.includes("|") ? activeStr.split("|") : [];
      const activeSectionId = activeParts.length >= 2 ? activeParts[0] : null;
      const activeItemId =
        activeParts.length >= 2 ? activeParts.slice(1).join("|") : null;
      if (!activeSectionId || !activeItemId) return;
      const activeSection = form.sections.find((s) => s.id === activeSectionId);
      const activeItem = activeSection?.items.find(
        (it) => it.id === activeItemId
      );
      if (!activeItem) return;

      const overIsSection = form.sections.some((s) => s.id === overStr);
      if (overIsSection) {
        setForm((prev) => ({
          ...prev,
          sections: prev.sections.map((s) => {
            if (s.id === activeSectionId) {
              return {
                ...s,
                items: s.items.filter((it) => it.id !== activeItemId),
              };
            }
            if (s.id === overStr) {
              return { ...s, items: [...s.items, activeItem] };
            }
            return s;
          }),
        }));
        return;
      }

      const overParts = overStr.includes("|") ? overStr.split("|") : [];
      const overSectionId = overParts.length >= 2 ? overParts[0] : null;
      const overItemId =
        overParts.length >= 2 ? overParts.slice(1).join("|") : null;
      if (!overSectionId || !overItemId) return;

      setForm((prev) => {
        const fromSection = prev.sections.find((s) => s.id === activeSectionId);
        const toSection = prev.sections.find((s) => s.id === overSectionId);
        if (!fromSection || !toSection) return prev;
        const fromItems = fromSection.items.filter(
          (it) => it.id !== activeItemId
        );
        const toItems = [...toSection.items];
        const overIndex = toSection.items.findIndex(
          (it) => it.id === overItemId
        );
        const insertIndex = overIndex >= 0 ? overIndex : toItems.length;
        if (activeSectionId === overSectionId) {
          const fromIndex = fromSection.items.findIndex(
            (it) => it.id === activeItemId
          );
          const reordered = arrayMove(
            fromSection.items,
            fromIndex,
            overIndex >= 0 ? overIndex : fromSection.items.length - 1
          );
          return {
            ...prev,
            sections: prev.sections.map((s) =>
              s.id === activeSectionId ? { ...s, items: reordered } : s
            ),
          };
        }
        toItems.splice(insertIndex, 0, activeItem);
        return {
          ...prev,
          sections: prev.sections.map((s) => {
            if (s.id === activeSectionId) return { ...s, items: fromItems };
            if (s.id === overSectionId) return { ...s, items: toItems };
            return s;
          }),
        };
      });
    },
    [form.sections]
  );

  const handleSave = async () => {
    const boardName = (form.boardName ?? "").toString().trim();
    const currency = (form.currency ?? "").toString().trim();
    if (!boardName) {
      enqueueSnackbar(
        t("pages.menuManager.boardNameRequired", {
          defaultValue: "Board name is required.",
        }),
        { variant: "warning" }
      );
      setSelectedTab("settings");
      return;
    }
    if (!currency) {
      enqueueSnackbar(
        t("pages.menuManager.currencyRequired", {
          defaultValue: "Currency is required.",
        }),
        { variant: "warning" }
      );
      setSelectedTab("settings");
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        ...form,
        boardName,
        currency,
        ...(isCreate ? {} : { id: boardId }),
      };
      await putMenuBoard(payload);
      enqueueSnackbar(
        t("notifications.wasSuccessful", {
          defaultValue: "Saved successfully.",
        }),
        { variant: "success" }
      );
      if (isCreate) {
        navigate("/customer/dsok/boards", { replace: true });
      }
    } catch (e) {
      const body = e?.response?.data;
      const message =
        (body && typeof body === "object" && body.message) ||
        (typeof body === "string" ? body : null) ||
        e?.message ||
        t("notifications.wasFailed", { defaultValue: "Save failed." });
      setSaveError({
        message,
        code:
          (body && typeof body === "object" && body.code) ??
          e?.response?.status,
        status: body && typeof body === "object" ? body.status : null,
      });
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const pageTitle = t("pages.menuManager.title", {
    defaultValue: "Menu Manager",
  });
  const createLabel = t("pages.menuManager.createBoard", {
    defaultValue: "Create Menu Board",
  });

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <Typography color="text.secondary">Loading…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          mb: 2,
          "& a": { color: "text.secondary", textDecoration: "none" },
          "& a:hover": { textDecoration: "underline" },
        }}
      >
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link to="/customer/dsok/boards">{pageTitle}</Link>
        <Typography color="text.primary">
          {isCreate ? createLabel : form.boardName || "—"}
        </Typography>
      </Breadcrumbs>

      {saveError && (
        <Alert
          severity="error"
          onClose={() => setSaveError(null)}
          sx={{ mb: 2 }}
        >
          <Typography component="span" variant="body2" fontWeight={600}>
            {saveError.message}
          </Typography>
          {(saveError.code != null || saveError.status) && (
            <Typography
              component="span"
              variant="caption"
              display="block"
              sx={{ mt: 0.5, opacity: 0.9 }}
            >
              {saveError.code != null && `HTTP ${saveError.code}`}
              {saveError.code != null && saveError.status && " · "}
              {saveError.status != null && String(saveError.status)}
            </Typography>
          )}
        </Alert>
      )}

      <Div sx={{ width: "100%" }}>
        <JumboCardQuick sx={{ width: "100%" }}>
          <TabContext value={selectedTab}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList onChange={(_, val) => setSelectedTab(val)}>
                <Tab
                  label={t("pages.menuManager.tabSettings", {
                    defaultValue: "Nastavenia",
                  })}
                  value="settings"
                />
                <Tab
                  label={t("pages.menuManager.tabItems", {
                    defaultValue: "Položky",
                  })}
                  value="items"
                />
              </TabList>
            </Box>

            <TabPanel value="settings" sx={{ px: 2, py: 2 }}>
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={2}
                  alignItems="flex-start"
                  sx={{ width: "100%" }}
                >
                  <TextField
                    label={t("pages.menuManager.boardNameLabel", {
                      defaultValue: "Board name",
                    })}
                    value={form.boardName}
                    onChange={handleChange("boardName")}
                    required
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 180, flex: "1 1 200px" }}
                  />
                  <Autocomplete
                    multiple
                    size="small"
                    options={allCustomerLanguages}
                    getOptionLabel={(opt) => opt?.label ?? opt?.code ?? ""}
                    isOptionEqualToValue={(a, b) => a?.code === b?.code}
                    value={languages}
                    onChange={(_, newValue) => {
                      setSelectedLanguageCodes(
                        (newValue || []).map((l) => l.code)
                      );
                    }}
                    sx={{ minWidth: 200, flex: "1 1 220px" }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("pages.menuManager.languagesLabel", {
                          defaultValue: "Vyber jazykov",
                        })}
                        placeholder={
                          languages.length === 0
                            ? t("pages.menuManager.languagesPlaceholder", {
                                defaultValue: "Vyberte jazyky",
                              })
                            : ""
                        }
                      />
                    )}
                  />
                  <TextField
                    type="number"
                    size="small"
                    label={t(
                      "pages.menuManager.languageRotationIntervalLabel",
                      {
                        defaultValue: "Interval rotácie jazykov (s)",
                      }
                    )}
                    value={form.displayLanguagesCount ?? 1}
                    onChange={handleChange("displayLanguagesCount")}
                    inputProps={{ min: 1, max: 3600, step: 1 }}
                    variant="outlined"
                    sx={{ minWidth: 100, flex: "0 1 140px" }}
                  />
                </Stack>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={2}
                  alignItems="flex-start"
                  sx={{ width: "100%" }}
                >
                  <TextField
                    label={t("pages.menuManager.currencyLabel", {
                      defaultValue: "Mena",
                    })}
                    value={form.currency}
                    onChange={handleChange("currency")}
                    required
                    variant="outlined"
                    size="small"
                    placeholder="€"
                    inputProps={{ maxLength: 3 }}
                    sx={{ width: 180, minWidth: 180, maxWidth: "100%" }}
                  />
                  <FormControl
                    component="fieldset"
                    sx={{ minWidth: 180, flex: "1 1 200px" }}
                  >
                    <FormLabel component="legend" sx={{ mb: 0.5 }}>
                      {t("pages.menuManager.displayTypeLabel", {
                        defaultValue: "Typ zobrazenia",
                      })}
                    </FormLabel>
                    <RadioGroup
                      row
                      value={form.displayType === "grid" ? "grid" : "list"}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          displayType: e.target.value,
                        }))
                      }
                    >
                      <FormControlLabel
                        value="list"
                        control={<Radio size="small" />}
                        label={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <ViewListIcon fontSize="small" />
                            <span>
                              {t("pages.menuManager.displayTypeList", {
                                defaultValue: "List",
                              })}
                            </span>
                          </Stack>
                        }
                        sx={{ mr: 2 }}
                      />
                      <FormControlLabel
                        value="grid"
                        control={<Radio size="small" />}
                        label={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <GridViewIcon fontSize="small" />
                            <span>
                              {t("pages.menuManager.displayTypeGrid", {
                                defaultValue: "Grid",
                              })}
                            </span>
                          </Stack>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                </Stack>
                <Stack direction="row" spacing={3} alignItems="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!form.showProductDescription}
                        onChange={handleChange("showProductDescription")}
                      />
                    }
                    label={t("pages.menuManager.showProductDescription", {
                      defaultValue: "Zobraziť popis produktov",
                    })}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!form.showProductImages}
                        onChange={handleChange("showProductImages")}
                      />
                    }
                    label={t("pages.menuManager.showProductImages", {
                      defaultValue: "Zobraziť obrázky",
                    })}
                  />
                </Stack>
              </Stack>
            </TabPanel>

            <TabPanel value="items" sx={{ px: 2, py: 2 }}>
              <Stack spacing={2}>
                <Button
                  type="button"
                  startIcon={<AddBoxIcon sx={{ fontSize: 18 }} />}
                  variant="outlined"
                  size="small"
                  onClick={handleAddSection}
                  sx={{
                    alignSelf: "flex-start",
                    py: 0.5,
                    fontSize: "0.8125rem",
                  }}
                >
                  {t("pages.menuManager.addSection", {
                    defaultValue: "Pridať sekciu",
                  })}
                </Button>

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => {
                    const { active, over } = event;
                    if (!over || active.id === over.id) return;
                    const activeId = String(active.id);
                    const overId = String(over.id);
                    const activeIsSection = form.sections.some(
                      (s) => s.id === activeId
                    );
                    const overIsSection = form.sections.some(
                      (s) => s.id === overId
                    );
                    if (activeIsSection && overIsSection) {
                      handleReorderSections(activeId, overId);
                    } else if (!activeIsSection) {
                      handleItemsDragEnd(event);
                    }
                  }}
                >
                  <SortableContext
                    items={[
                      ...form.sections.map((s) => s.id),
                      ...allItemSortIds,
                    ]}
                    strategy={verticalListSortingStrategy}
                  >
                    {form.sections.map((section) => (
                      <SortableSectionBlock
                        key={section.id}
                        section={section}
                        languages={languages}
                        dsokProducts={dsokProducts}
                        lang={lang}
                        showProductImages={form.showProductImages}
                        autocompleteKey={autocompleteKeys[section.id] || 0}
                        getOptionDisplayName={getOptionDisplayName}
                        onSectionTitleChange={handleSectionTitleChange}
                        onAddProduct={handleAddProductToSection}
                        onRemoveProduct={handleRemoveProduct}
                        onUpdateProduct={handleUpdateProduct}
                        onItemsDragEnd={handleItemsDragEnd}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </Stack>
            </TabPanel>
          </TabContext>

          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            sx={{ p: 2, borderTop: 1, borderColor: "divider" }}
          >
            <Button
              component={Link}
              to="/customer/dsok/boards"
              variant="outlined"
              size="small"
              startIcon={<FastRewindIcon />}
              sx={{ textDecoration: "none" }}
            >
              {t("buttons.back")}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<BrowserUpdatedIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {t("buttons.save", { defaultValue: "Save" })}
            </Button>
          </Stack>
        </JumboCardQuick>
      </Div>
    </Box>
  );
};

function SortableSectionBlock({
  section,
  languages,
  dsokProducts,
  lang,
  showProductImages,
  autocompleteKey,
  getOptionDisplayName,
  onSectionTitleChange,
  onAddProduct,
  onRemoveProduct,
  onUpdateProduct,
  onItemsDragEnd,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });
  const { t } = useTranslation();
  const [productInput, setProductInput] = useState("");

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const itemSortIds = section.items.map((it) => `${section.id}|${it.id}`);

  return (
    <div ref={setNodeRef} style={style}>
      <Box
        sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Div
            {...attributes}
            {...listeners}
            sx={{ cursor: "grab", color: "text.secondary" }}
          >
            <DragIndicatorIcon fontSize="small" />
          </Div>
          <Typography variant="subtitle1" fontWeight={600}>
            {t("pages.menuManager.sectionTitle", { defaultValue: "Sekcia" })}
          </Typography>
        </Stack>
        <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
          {languages.map((l) => (
            <TextField
              key={l.code}
              size="small"
              label={`${t("pages.menuManager.sectionNameLabel", {
                defaultValue: "Názov",
              })} (${l.label || l.code})`}
              value={section.titleByLang?.[l.code] ?? ""}
              onChange={(e) =>
                onSectionTitleChange(section.id, l.code, e.target.value)
              }
              sx={{ minWidth: 180, flex: "1 1 180px" }}
            />
          ))}
        </Stack>
        <FormControl sx={{ mb: 2, maxWidth: "50%" }}>
          <Autocomplete
            key={`${section.id}-${autocompleteKey}`}
            size="small"
            options={dsokProducts}
            getOptionLabel={(opt) => getOptionDisplayName(opt, lang)}
            isOptionEqualToValue={(a, b) =>
              (a?.id ?? a?._id) === (b?.id ?? b?._id)
            }
            filterOptions={(options, state) =>
              (options || []).filter((opt) =>
                String(getOptionDisplayName(opt, lang) || "")
                  .toLowerCase()
                  .includes(String(state.inputValue || "").toLowerCase())
              )
            }
            value={null}
            inputValue={productInput}
            onInputChange={(_, value) => setProductInput(value ?? "")}
            onChange={(_, option) => {
              if (option) {
                onAddProduct(section.id, option);
                setProductInput("");
              }
            }}
            renderOption={(props, option) => (
              <li {...props} key={option?.id ?? option?._id}>
                {getOptionDisplayName(option, lang)}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t("pages.menuManager.addProduct", {
                  defaultValue: "Pridať produkt",
                })}
              />
            )}
          />
        </FormControl>
        <SortableContext
          items={itemSortIds}
          strategy={verticalListSortingStrategy}
        >
          {section.items.map((item) => (
            <MenuBoardProductRow
              key={item.id}
              item={item}
              itemSortId={`${section.id}|${item.id}`}
              lang={lang}
              languages={languages}
              showImage={!!showProductImages}
              onUpdate={(field, value) =>
                onUpdateProduct(section.id, item.id, field, value)
              }
              onRemove={(itemId) => onRemoveProduct(section.id, itemId)}
            />
          ))}
        </SortableContext>
      </Box>
    </div>
  );
}

export default MenuBoardEdit;
